/**
 * A DATE's figures — THE one owner of the day-for-day valuation (Chris,
 * 2026-09-02: "ensure your modelled season uses the SALES as they were — like
 * for like, day for day from the last season"), and of the two rules every
 * reader of a date must apply the same way (Chris, 2026-09-03):
 *
 *   1. PASSENGERS are admissions — people, counted once. The engine's `carried`
 *      counts each originating passenger once (engine.ts); the unoptimised side
 *      is the basis day's real admissions. Same unit both sides, so the
 *      modelled season can never read as 160% of the sales it was poured from.
 *
 *   2. A like-for-like basis day with NO timed departure sold is a day the
 *      railway ran NO standard services (a gala, an event, or not running at
 *      all — the tills' untimed admissions that day are event traffic leaking
 *      through a blank booking type). On such a date BOTH sides show 0
 *      services, 0 passengers, £0 revenue and the FIXED cost only — every
 *      variable cost removed ("set the services to 0 and remove the variable
 *      costs. Clearly we didn't run standard services on that day").
 *
 * Used by: the on-screen WSS cells and totals, the season table (yearRows),
 * the board-paper WSS cells and season tiles, and both workbooks — all through
 * `daySide`, so they agree by construction (CLAUDE.md §3: never a second owner).
 */
import type { Departure, DemandRow, Network, EngineParams, SlotDailyRow } from './types'
import type { DateActual } from './dateActuals'
import { mkSlots } from './demand'
import { runScenario } from './engine'

/** No timed departure sold on the basis day ⇒ no standard services ran. */
export const isNoStandardServiceDay = (a: DateActual | null | undefined): boolean => !!a && a.timedPax === 0

/** The label every surface prints for such a date. */
export const NO_SERVICE_LABEL = 'no standard services'

export function valueDepsOnDay(
  net: Network,
  deps: Departure[],
  day: { slotRows: SlotDailyRow[]; untimedNet: number; untimedPax: number },
  pr: EngineParams,
  fareMult: number,
  /** Who is one-way on TODAY's timetable for this day-type (engine.ts::
   *  oneWayShares, banked with the plan) — the same people the build treated
   *  as one-way, so a date's cell and the build agree. */
  oneWayToday?: Record<string, number>,
  /** The day's demand scaled (a Firecon day: 1 − the % down on the like day;
   *  firecon.ts::fireconScale). 1 = the like day as it was. */
  demandScale = 1,
): { pax: number; revenue: number; turnedAway: number } {
  const k = Number.isFinite(demandScale) && demandScale >= 0 ? demandScale : 1
  const rows: DemandRow[] = day.slotRows.map((r) => ({
    month_no: r.month_no, yr: r.yr, day_class: r.day_class,
    // The slot's own origin/destination (the MERAC session label) — a
    // Pickering–Grosmont booking must not be seated as a Whitby one.
    direction: r.direction, dep_time: r.dep_time, service: r.service ?? null,
    days_run: 1, pax: r.pax * k, net: r.net * k,
  }))
  const stamp = (slots: ReturnType<typeof mkSlots>) => oneWayToday
    ? slots.map((s) => { const sh = oneWayToday[`${s.minutes}|${s.o}|${s.d}`]; return sh != null && sh > 0 ? { ...s, oneWayShare: sh } : s })
    : slots
  const north = stamp(mkSlots(net, rows, 'Northbound', 1))
  const south = stamp(mkSlots(net, rows, 'Southbound', 1))
  const enabled = deps.filter((d) => d.enabled)
  const svc = enabled.filter((d) => !d.positioning).length
  const sc = runScenario(net, north, south, enabled, pr)
  // Walk-ups (untimed) travel whenever trains run at all — earned by the day,
  // not a slot; a day with no services earns none of it (D4).
  return {
    pax: sc.carried + (svc > 0 ? day.untimedPax * k : 0),
    revenue: sc.revenue + (svc > 0 ? day.untimedNet * k * fareMult : 0),
    // Turned away by timing (Chris, 2026-09-07): the day's demand that did not
    // travel on this timetable — no train within the tolerance once the
    // Recapture discount applies, or the nearest train had no way home. With no
    // trains at all, that is the whole of the day's timed demand.
    turnedAway: sc.lostSales,
  }
}

// ---------------------------------------------------------------------------
//  One side of one date — the shared arithmetic.
// ---------------------------------------------------------------------------

/** How a date's passengers/revenue were arrived at — printed, never implied. */
export type DayFigureKind =
  | 'actual'      // the basis day's real sales (unoptimised side)
  | 'valued'      // the designed timetable valued on the basis day's demand
  | 'average'     // no like-for-like basis day — the side's day-type/month average, labelled
  | 'no-service'  // basis day ran no standard services — zeroed, fixed cost only

export interface DaySideFigures {
  services: number
  sets: number
  steamN: number
  dieselN: number
  miles: number
  pax: number
  revenue: number
  /** Passengers turned away by timing on this side's timetable that day; null
   *  when the side's figure is unknown (an older snapshot's average). */
  turnedAway: number | null
  /** Each register engine's TIMETABLED miles that day; null when nothing runs. */
  engineMiles: Record<string, number> | null
  /** A Firecon day (firecon.ts): steam stood down, this day of the run, the sales % down. */
  firecon?: { day: number; pct: number }
  /** The availability calendar's effect on this date (fleetAvailability.ts). */
  fleet?: DayFleetNote
  /** A failure day — no engine for a turn; valued as a non-running day, marked red. */
  failure?: boolean
  /** The day's share of its MONTH's fixed cost — the line runs whether or not
   *  a standard service does (Chris, 2026-09-04). */
  fixed: number
  /** The cost of running this day's trains: per running day, per turn, per
   *  train-mile, per passenger. Zero when no standard service runs. */
  variable: number
  /** fixed + variable */
  costs: number
  profit: number
  kind: DayFigureKind
}

/** What the timetable that would run this date looks like (per plan/side). */
export interface DayPlanShape {
  services: number
  sets: number
  steamN: number
  dieselN: number
  /** Train-miles that timetable runs in a day (engine per-segment mileage). */
  miles: number
  /** TIMETABLED miles per register engine that day (costFromDeps.engineMiles). */
  engineMiles?: Record<string, number>
  /** The day's fleet (fleetAvailability.ts): steam turns run diesel for want of a
   *  steam engine, turns with no engine at all, and the engines out that day. */
  fleet?: DayFleetNote
}
/** What the availability calendar did to one date. */
export interface DayFleetNote {
  swapped: number; noEngine: number; out: { label: string; reason: string }[]
  /** A FAILURE DAY (engineRoster.ts): a turn no engine of any traction could take —
   *  the timetable cannot run as designed, so the date is valued as a
   *  non-running day and marked red (Chris, 2026-09-11). */
  failure?: boolean
}

/**
 * ONE date, ONE side. `act` is the like-for-like basis day (null = unmatched);
 * `valued` is what this date is worth on that day (the real sales for the
 * unoptimised side, valueDepsOnDay for the optimised side); `average` is the
 * labelled fallback when there is no basis day. `cost(pax, noService)` prices
 * the day as { fixed, variable } — callers pass the accounts model (P&L) or the
 * rate card; `fixed` is the day's share of its month's fixed cost and MUST be
 * charged even when `noService` is true, `variable` MUST be 0 then.
 */
export function daySide(args: {
  act: DateActual | null
  plan: DayPlanShape
  valued: { pax: number; revenue: number; turnedAway?: number } | null
  valuedKind: 'actual' | 'valued'
  average: { pax: number; revenue: number; turnedAway?: number }
  cost: (pax: number, noService: boolean) => { fixed: number; variable: number }
  /** The date is a Firecon day — carried onto the figures so every surface can mark it. */
  firecon?: { day: number; pct: number }
}): DaySideFigures {
  const { act, plan, valued, valuedKind, average, cost, firecon } = args
  const tag = { ...(firecon ? { firecon } : {}), ...(plan.fleet ? { fleet: plan.fleet } : {}), ...(plan.fleet?.failure ? { failure: true } : {}) }
  if (isNoStandardServiceDay(act)) {
    const c = cost(0, true)
    const costs = c.fixed
    // Nothing runs: no turn was swapped or left without an engine; the engines out that day still are.
    const idle = plan.fleet ? { fleet: { ...plan.fleet, swapped: 0, noEngine: 0 } } : {}
    return { services: 0, sets: 0, steamN: 0, dieselN: 0, miles: 0, pax: 0, revenue: 0, turnedAway: 0, engineMiles: null, fixed: c.fixed, variable: 0, costs, profit: -costs, kind: 'no-service', ...tag, ...idle }
  }
  const v = valued ?? average
  const kind: DayFigureKind = valued ? valuedKind : 'average'
  // A plan with no services runs no rakes and no engines (a "modelled not run"
  // date) — never print a set or a loco against zero trains, and charge it no
  // variable cost: the fixed cost of the day still stands.
  const runs = plan.services > 0
  const c = cost(v.pax, false)
  const variable = runs ? c.variable : 0
  const costs = c.fixed + variable
  return { services: plan.services, sets: runs ? plan.sets : 0, steamN: runs ? plan.steamN : 0, dieselN: runs ? plan.dieselN : 0, miles: runs ? plan.miles : 0, pax: v.pax, revenue: v.revenue, turnedAway: v.turnedAway ?? null, engineMiles: runs ? (plan.engineMiles ?? null) : null, fixed: c.fixed, variable, costs, profit: v.revenue - costs, kind, ...tag }
}

/** A month/season roll-up of `daySide` results — the same sums everywhere. */
export interface SideTotals {
  days: number
  noServiceDays: number
  averageDays: number
  turns: number
  miles: number
  pax: number
  revenue: number
  /** Passengers turned away by timing, summed over the days that carry the figure. */
  turnedAway: number
  /** How many of `days` carried a turned-away figure (a side re-based from an
   *  older snapshot's average may not). */
  turnedAwayDays: number
  fixed: number
  variable: number
  costs: number
  profit: number
  /** TIMETABLED miles per register engine over the days, and the days each worked. */
  engineMiles: Record<string, number>
  engineDays: Record<string, number>
  /** Firecon days among the days (steam stood down). */
  fireconDays: number
  /** The availability calendar over the days: steam turns run diesel, turns with no engine, engine-days out. */
  swappedTurns: number
  noEngineTurns: number
  outEngineDays: number
  /** Failure days — the timetable could not be engined, valued as non-running. */
  failureDays: number
}
export const emptyTotals = (): SideTotals => ({ days: 0, noServiceDays: 0, averageDays: 0, turns: 0, miles: 0, pax: 0, revenue: 0, turnedAway: 0, turnedAwayDays: 0, fixed: 0, variable: 0, costs: 0, profit: 0, engineMiles: {}, engineDays: {}, fireconDays: 0, swappedTurns: 0, noEngineTurns: 0, outEngineDays: 0, failureDays: 0 })
export function addDay(t: SideTotals, d: DaySideFigures): void {
  t.days += 1
  if (d.firecon) t.fireconDays += 1
  if (d.failure) t.failureDays += 1
  if (d.fleet) { t.swappedTurns += d.fleet.swapped; t.noEngineTurns += d.fleet.noEngine; t.outEngineDays += d.fleet.out.length }
  if (d.kind === 'no-service') t.noServiceDays += 1
  if (d.kind === 'average') t.averageDays += 1
  t.turns += d.services
  t.miles += d.miles
  t.pax += d.pax
  t.revenue += d.revenue
  if (d.turnedAway != null) { t.turnedAway += d.turnedAway; t.turnedAwayDays += 1 }
  if (d.engineMiles) {
    for (const [k, v] of Object.entries(d.engineMiles)) {
      t.engineMiles[k] = (t.engineMiles[k] ?? 0) + v
      if (v > 0) t.engineDays[k] = (t.engineDays[k] ?? 0) + 1
    }
  }
  t.fixed += d.fixed
  t.variable += d.variable
  t.costs += d.costs
  t.profit += d.profit
}
